import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Target,
  MessageSquare,
  Users,
  RefreshCw,
  Zap,
  Shield,
} from 'lucide-react';

const dimensions = [
  { key: 'conscientiousness', label: 'Structure Preferences', score: 85, icon: Target },
  { key: 'communication', label: 'Communication', score: 92, icon: MessageSquare },
  { key: 'collaboration', label: 'Collaboration', score: 78, icon: Users },
  { key: 'adaptability', label: 'Adaptability', score: 88, icon: RefreshCw },
  { key: 'ownership', label: 'Ownership', score: 82, icon: Zap },
  { key: 'resilience', label: 'Resilience', score: 75, icon: Shield },
];

function getScoreColor(score: number) {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-destructive';
}

export function LandingWorkStyleDemo() {
  const overallScore = Math.round(
    dimensions.reduce((sum, d) => sum + d.score, 0) / dimensions.length
  );

  return (
    <div className="space-y-6">
      {/* Overall Fit Score */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-primary/5 border border-primary/20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Target className="w-6 h-6 text-primary" />
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Overall Fit Score</div>
            <div className="text-2xl font-bold text-heading">{overallScore}%</div>
          </div>
        </div>
        <Badge className="bg-green-100 text-green-700 border-green-200">
          Strong Fit
        </Badge>
      </div>

      {/* Dimension Bars */}
      <div className="grid gap-3">
        {dimensions.map((dimension) => {
          const Icon = dimension.icon;
          return (
            <div key={dimension.key} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{dimension.label}</span>
                </div>
                <span className={cn('text-sm font-medium', getScoreColor(dimension.score))}>
                  {dimension.score}%
                </span>
              </div>
              <Progress value={dimension.score} className="h-2" />
            </div>
          );
        })}
      </div>

      {/* Quality Indicators */}
      <div className="flex items-center justify-center gap-4 pt-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          Consistent responses
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          No anomalies detected
        </div>
      </div>
    </div>
  );
}
